import { useState } from 'react';
import { api } from '../services/api';
import { bucketLabel } from '../utils/bucketLabels';
import { trackRun } from '../stores/runStore';
import { RunTimer } from './RunIndicator';
import './AddWordBox.css';

// One-step Add word (Odin's ruling 2026-10-22): type it, pick a bucket, done.
// POST /api/wordbank/add-one saves the word approved in that bucket (the same
// stamp and word_moves row a Triage move gives), then runs the Word Bank vet
// and score. A failed score keeps the word and offers Re-run
// (POST /api/wordbank/:id/rescore). A word already in the bank is not touched;
// the box names its bucket and offers to move it (POST /api/wordbank/:id/bucket).
// Used on the Word Bank page and in the Word Binder. Add and Re-run call the
// scorer model, so both report to the run store and pulse with a timer (2026-10-22).

type Bucket = 'Game' | 'Related' | 'Bench' | 'Rejected';
const BUCKETS: Bucket[] = ['Game', 'Related', 'Bench', 'Rejected'];

export interface ScoreResult {
  ok: boolean;
  error?: string;
  vet_verdict?: string | null;
  stopped_at?: string | null;
  difficulty?: { score: number | null; reason: string | null } | null;
}

interface AddReply {
  created?: boolean;
  exists?: boolean;
  id: string;
  word: string;
  bucket: Bucket;
  approved?: boolean;
  score?: ScoreResult;
}

/** One line for a vet-and-score result. */
export function scoreText(word: string, s: ScoreResult): string {
  if (!s.ok) return `Scoring "${word}" failed: ${s.error || 'no reason given'}`;
  if (s.stopped_at === 'vet') return `Vetting said ${s.vet_verdict || 'no'} for "${word}", so it was not scored.`;
  if (s.stopped_at === 'vet_incomplete') return `Vetting found no definition or part of speech for "${word}", so it was not scored.`;
  const d = s.difficulty;
  return d && d.score !== null ? `Difficulty ${d.score} of 5${d.reason ? `: ${d.reason}` : ''}` : `"${word}" scored.`;
}

/** Re-run is offered whenever the word has no scores. */
export function needsRerun(s: ScoreResult | undefined): boolean {
  return !s || !s.ok || !!s.stopped_at;
}

export default function AddWordBox({ compact = false, onChanged }: { compact?: boolean; onChanged?: () => void }) {
  const [text, setText] = useState('');
  const [bucket, setBucket] = useState<Bucket>('Game');
  const [busy, setBusy] = useState(false);
  // When this box's scoring run started; null when none is running.
  const [scoring, setScoring] = useState<number | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  // An existing word the box can move, or a saved word the box can re-score.
  const [exists, setExists] = useState<{ id: string; word: string; from: Bucket; to: Bucket } | null>(null);
  const [rerun, setRerun] = useState<{ id: string; word: string } | null>(null);

  const reset = () => { setMessage(null); setExists(null); setRerun(null); };

  const showScore = (id: string, word: string, saved: string, s: ScoreResult | undefined) => {
    const line = s ? scoreText(word, s) : 'It was not scored.';
    setMessage({ ok: !!s?.ok && !s.stopped_at, text: `${saved} ${line}` });
    setRerun(needsRerun(s) ? { id, word } : null);
  };

  const add = async () => {
    const word = text.trim();
    if (!word || busy) return;
    setBusy(true);
    setScoring(Date.now());
    reset();
    try {
      const r = await trackRun(
        { kind: 'score', wordId: null, word, label: 'Add word scoring' },
        () => api.post('/api/wordbank/add-one', { word, bucket }) as Promise<AddReply>,
        (x) => (x.exists ? `"${x.word}" is already in the Word Bank. Nothing was added.` : `Add word done: ${x.word}`),
      );
      if (r.exists) {
        const where = `${bucketLabel(r.bucket)}${r.approved ? '' : ', unapproved'}`;
        setMessage({ ok: false, text: `"${r.word}" is already in ${where}. Nothing was added.` });
        if (r.bucket !== bucket) setExists({ id: r.id, word: r.word, from: r.bucket, to: bucket });
        return;
      }
      setText('');
      showScore(r.id, r.word, `"${r.word}" added to ${bucketLabel(r.bucket)}, approved.`, r.score);
      onChanged?.();
    } catch (err) {
      setMessage({ ok: false, text: (err as Error).message });
    } finally {
      setBusy(false);
      setScoring(null);
    }
  };

  const moveExisting = async () => {
    if (!exists || busy) return;
    setBusy(true);
    try {
      await api.post(`/api/wordbank/${exists.id}/bucket`, { bucket: exists.to });
      setMessage({ ok: true, text: `"${exists.word}" moved from ${bucketLabel(exists.from)} to ${bucketLabel(exists.to)}, approved.` });
      setExists(null);
      onChanged?.();
    } catch (err) {
      setMessage({ ok: false, text: (err as Error).message });
    } finally {
      setBusy(false);
    }
  };

  const rescore = async () => {
    if (!rerun || busy) return;
    setBusy(true);
    setScoring(Date.now());
    try {
      const r = await trackRun(
        { kind: 'score', wordId: rerun.id, word: rerun.word, label: 'Re-run scoring' },
        () => api.post(`/api/wordbank/${rerun.id}/rescore`, {}) as Promise<{ id: string; word: string; score: ScoreResult }>,
      );
      showScore(r.id, r.word, 'Re-run done.', r.score);
      onChanged?.();
    } catch (err) {
      setMessage({ ok: false, text: (err as Error).message });
    } finally {
      setBusy(false);
      setScoring(null);
    }
  };

  return (
    <div className={`awb ${compact ? 'awb-compact' : ''}`}>
      <form className="awb-form" onSubmit={(e) => { e.preventDefault(); void add(); }}>
        <input
          className="awb-input"
          placeholder="Add a word"
          aria-label="Word to add"
          value={text}
          onChange={(e) => { setText(e.target.value); if (message) reset(); }}
          disabled={busy}
        />
        <select className="awb-select" aria-label="Bucket" value={bucket} onChange={(e) => setBucket(e.target.value as Bucket)} disabled={busy}>
          {BUCKETS.map((b) => <option key={b} value={b}>{bucketLabel(b)}</option>)}
        </select>
        <button type="submit" className={`awb-btn awb-primary ${scoring ? 'run-pulse' : ''}`} disabled={busy || !text.trim()}>
          {scoring ? <>Scoring… <RunTimer startedAt={scoring} /></> : busy ? 'Working…' : 'Add'}
        </button>
      </form>
      {busy && <p className="awb-msg">Vetting and scoring can take up to a minute.</p>}
      {message && !busy && (
        <div className={`awb-msg ${message.ok ? 'awb-ok' : 'awb-fail'}`} role="status">
          <span>{message.text}</span>
          {exists && (
            <button type="button" className="awb-btn" onClick={moveExisting}>
              Move it to {bucketLabel(exists.to)}
            </button>
          )}
          {rerun && (
            <button type="button" className="awb-btn" onClick={rescore}>Re-run</button>
          )}
        </div>
      )}
    </div>
  );
}
